/**
 * Shared types and constants used by the project data builders.
 */

// Free-field polygon ID constant (matches useModuleState.ts)
export const FREE_FIELD_POLYGON_ID = -1;

export interface SolarModuleState {
  position: [number, number];
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  rotation?: number;
  segmentId?: number;
}

export interface ModuleSegmentState {
  id: number;
  bounds: [number, number][];
  rotation?: number;
  azimuth?: number;
  modules: SolarModuleState[];
  config?: {
    moduleSpacing: number;
    rowSpacing: number;
    targetCount: number | null;
  };
}

export interface PolygonState {
  id: number;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  area_m2: number;
}

export interface PanelState {
  brand: string;
  model: string;
  power_watts: number;
  width_mm: number;
  height_mm: number;
  efficiency?: number;
  cell_type?: string;
  category?: string;
}

export interface ProjectStateInput {
  // Location
  currentProjectId: string | null;
  currentRPU: string | null;
  currentAddress: string;
  pinPosition: { lat: number; lng: number } | null;

  // Cache
  geotiffCacheId: string | null;
  buildingInsightsId: string | null;

  // Polygon data
  polygonData: { polygons: PolygonState[] } | null;
  selectedPolygon: number | null;
  editedPolygonCoords: Record<number, [number, number][]>;

  // Multi-roof
  establishedRoofIds: Set<number>;
  activeRoofId: number | null;

  // Snap edges
  snapEdgeIndex: Record<number, number>;
  snapEdgeAzimuth: Record<number, number>;
  manualAzimuth: Record<number, number | null>;

  // Obstructions
  obstructions: Record<number, [number, number][][]>;

  // Modules
  modules: Record<number, SolarModuleState[]>;
  moduleSegments: Record<number, ModuleSegmentState[]>;

  // Module config
  moduleWidth: number;
  moduleHeight: number;
  modulePowerWatts: number;
  moduleOrientation: 'portrait' | 'landscape';
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  edgeSetback: number;
  obstructionSetback: number;

  // Panel selection
  selectedPanel: PanelState | null;

  // Status
  projectStatus: 'draft' | 'final' | 'archived';

  // Utility functions passed in
  calculatePolygonArea: (coords: [number, number][]) => number;
}
